import { R2Config } from '../config/r2-config.js';
import { catalogJsonUrl } from '../js/config.js';
import { IndexedDBService } from './IndexedDBService.js';
import apiClient from './ApiClient.js';
import { fetchCache } from '../js/utils/fetchCache.js';

export class R2DataService {
  constructor() {
    this.dbService = new IndexedDBService();
    this.initialized = false;
  }

  async init() {
    if (this.initialized) return;
    await this.dbService.init();
    this.initialized = true;
  }

  async fetchData(url, storeName, forceRefresh = false, apiFallbackEndpoint = null) {
    await this.init();
    
    const isStale = forceRefresh || await this.dbService.isDataStale(storeName);
    if (!isStale) {
      return await this.dbService.getAllData(storeName);
    }

    try {
      // Public data is always fetched from CDN (worker no longer serves R2 files)
      const fullUrl = catalogJsonUrl(url);

      if (url.includes('characters_rankings.json')) {
        const data = await fetchCache.fetchCharacterRankings();
        const arrayData = Array.isArray(data) ? data : [data].filter(Boolean);
        await this.dbService.storeData(storeName, arrayData);
        return arrayData;
      }

      const data = await fetchCache.fetchJSON(fullUrl);
      const arrayData = Array.isArray(data) ? data : [data].filter(Boolean);
      await this.dbService.storeData(storeName, arrayData);
      return arrayData;
    } catch (error) {
      const cachedData = await this.dbService.getAllData(storeName);
      if (cachedData?.length) return cachedData;
      
      if (R2Config.api.enableFallback && apiFallbackEndpoint) {
        try {
          const apiResponse = await apiClient.get(apiFallbackEndpoint);
          const apiData = apiResponse.data || apiResponse;
          const arrayData = Array.isArray(apiData) ? apiData : [apiData].filter(Boolean);
          await this.dbService.storeData(storeName, arrayData);
          return arrayData;
        } catch {
          throw new Error(`R2 fetch failed and API fallback failed: ${error.message}`);
        }
      }
      
      throw error;
    }
  }

  getCharacterRankings(forceRefresh = false) {
    return this.fetchData(
      R2Config.paths.characters.rankings,
      R2Config.cache.stores.characters,
      forceRefresh,
      R2Config.api.endpoints.characters
    );
  }

  getCharacterDetails(forceRefresh = false) {
    return this.fetchData(
      R2Config.paths.characters.modal,
      R2Config.cache.stores.characterDetails,
      forceRefresh,
      R2Config.api.endpoints.characterDetails
    );
  }

  async getCharacterById(characterId, forceRefresh = false) {
    const numericId = typeof characterId === 'string' ? parseInt(characterId, 10) : characterId;
    const allDetails = await this.getCharacterDetails(forceRefresh);
    
    const character = allDetails.find(char => 
      char.id === numericId || 
      char.id === characterId || 
      char.id?.toString() === characterId.toString()
    );
    
    if (character) return character;
    
    if (R2Config.api.enableFallback) {
      const apiResponse = await apiClient.get(`${R2Config.api.endpoints.characterDetails}/${characterId}`);
      return apiResponse.data || apiResponse;
    }
    
    throw new Error(`Character with ID ${characterId} not found`);
  }

}
